import { useState } from 'react';
import QuoteModal from '@/components/QuoteModal';
import Seo from '@/components/Seo';
import { AVIATION_HANGAR_IMAGE, BRAND_LOGO_URL } from '@/data/servicesData';

const ABOUT_SERVICES = [
  'Padel court cleaning & maintenance',
  'Waterproofing and leak prevention',
  'Paving replacement and restoration',
  'Contractual maintenance programs',
  'Dedicated on-site cleaning teams',
  'Galvanised and Chromadek roof repairs',
  'Aviation facility cleaning support',
  'Garden and landscape service plans',
];

const AboutHero = () => (
  <div className="mb-24">
    <div className="blackline-text-card max-w-4xl splash-heading-bar" data-testid="about-hero-card">
      <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase leading-none text-white mb-8" data-testid="about-hero-title">
        GRIME DOESN'T WAIT.<br />
        BLOCKED GUTTERS<br />
        DON'T WARN YOU.
      </h1>
      <p className="font-oswald text-2xl md:text-3xl uppercase text-blackline-accent" data-testid="about-hero-subtitle">
        DIRTY BUILDINGS DON'T IMPRESS ANYONE.
      </p>
    </div>
  </div>
);

const AboutStory = () => (
  <div className="mb-24 border-l-4 border-blackline-accent pl-8 md:pl-12">
    <div className="blackline-text-card splash-heading-bar" data-testid="about-thats-why-card">
      <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-white mb-6" data-testid="about-thats-why-title">
        THAT'S WHY BLACKLINE CLEANING EXISTS.
      </h2>
      <p className="font-mono text-sm md:text-base text-zinc-400 mb-4">
        We provide contract-based exterior cleaning for residential complexes,
        commercial buildings, and industrial sites in the Johannesburg and Sandton areas — at rates residents don't fight
        and managers don't regret.
      </p>
    </div>
  </div>
);

const AboutWhatWeDo = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-24">
    <div className="blackline-text-card splash-heading-bar" data-testid="about-what-we-clean-card">
      <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white mb-8" data-testid="about-what-we-clean-title">
        WHAT WE DO BEST
      </h2>
      <div className="space-y-4 font-mono text-sm text-zinc-400">
        {ABOUT_SERVICES.map((service, idx) => (
          <div className="flex items-start gap-3" key={service} data-testid={`about-service-item-${idx}`}>
            <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
            <p className="uppercase">{service}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="border-l border-blackline-border pl-8">
      <div className="blackline-text-card splash-heading-bar" data-testid="about-service-area-card">
        <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white mb-8" data-testid="about-service-area-title">
          SERVICE AREA
        </h2>
        <div className="mb-6">
          <p className="font-oswald text-xl font-bold text-blackline-accent uppercase mb-2">
            JOHANNESBURG & SANDTON
          </p>
          <p className="font-mono text-sm text-zinc-400">
            We proudly serve the Johannesburg and Sandton areas, providing professional
            exterior cleaning services to residential complexes, commercial properties,
            and industrial sites throughout the region.
          </p>
        </div>
        <div className="border border-blackline-border p-6">
          <p className="font-mono text-sm text-zinc-400">
            <span className="text-blackline-accent">→</span> Local expertise, professional results
          </p>
        </div>
      </div>
    </div>
  </div>
);

const AboutWhyContract = () => (
  <div className="mb-24 border-t border-blackline-border pt-16">
    <div className="blackline-text-card splash-heading-bar" data-testid="about-why-contract-card">
      <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white mb-8" data-testid="about-why-contract-title">
        WHY CONTRACT-BASED?
      </h2>
      <p className="font-mono text-sm text-zinc-400 mb-6">
        Because emergency cleaning is expensive. And pointless.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4 font-mono text-sm text-zinc-400">
          <div className="flex items-start gap-3">
            <span className="text-blackline-accent">→</span>
            <p>Reactive call-outs cost more</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-blackline-accent">→</span>
            <p>Neglect causes damage</p>
          </div>
        </div>
        <div className="space-y-4 font-mono text-sm text-zinc-400">
          <div className="flex items-start gap-3">
            <span className="text-blackline-accent">→</span>
            <p>Residents complain</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-blackline-accent">→</span>
            <p>You get blamed</p>
          </div>
        </div>
      </div>
      <p className="font-mono text-sm text-white mt-6">
        Our scheduled maintenance stops problems BEFORE they show up.
      </p>
    </div>
  </div>
);

const AboutAviation = () => (
  <div className="mb-24">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
      <div className="blackline-text-card splash-heading-bar" data-testid="about-aviation-card">
        <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white mb-4" data-testid="about-aviation-title">
          AVIATION CLEANING NOW AVAILABLE
        </h2>
        <p className="font-mono text-sm text-zinc-300" data-testid="about-aviation-description">
          We now support hangars, aircraft exterior cleaning, compliance & sanitation support,
          and scheduled maintenance programs with the same BLACKLINE quality standards.
        </p>
      </div>
      <div className="border border-blackline-border overflow-hidden alive-division-card" data-testid="about-aviation-image-card">
        <img
          src={AVIATION_HANGAR_IMAGE}
          alt="BLACKLINE aviation cleaning services"
          className="w-full h-full object-cover before-after-image"
          loading="lazy"
          decoding="async"
          data-testid="about-aviation-image"
        />
      </div>
    </div>
  </div>
);

const AboutBodyCorporate = () => (
  <div className="border border-blackline-border p-8 md:p-12 mb-24 blackline-text-card splash-heading-bar" data-testid="about-body-corporates-card">
    <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-blackline-accent mb-6" data-testid="about-body-corporates-title">
      BODY CORPORATES - READ THIS
    </h2>
    <p className="font-mono text-sm text-zinc-400 mb-8">
      You manage budgets. People. Expectations.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-4 font-mono text-sm text-zinc-400">
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
          <p>Affordable monthly rates residents accept</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
          <p>No surprise invoices</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
          <p>No contractor chasing</p>
        </div>
      </div>
      <div className="space-y-4 font-mono text-sm text-zinc-400">
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
          <p>No "we'll get to it next month" excuses</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 bg-blackline-accent mt-2 flex-shrink-0" />
          <p>Cleaner buildings people actually notice</p>
        </div>
      </div>
    </div>
    <p className="font-mono text-sm text-white mt-8">
      Your job just got easier.
    </p>
  </div>
);

const AboutClosing = ({ onOpenQuote }) => (
  <div className="text-center">
    <div className="blackline-text-card inline-block splash-heading-bar" data-testid="about-one-company-card">
      <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase text-white mb-4" data-testid="about-one-company-title">
        ONE COMPANY. ONE CONTRACT.
      </h2>
      <p className="font-oswald text-2xl md:text-3xl uppercase text-blackline-accent mb-8" data-testid="about-one-company-subtitle">
        EVERYTHING HANDLED.
      </p>
      <p className="font-mono text-sm text-zinc-400 max-w-2xl mx-auto mb-12">
        Stop juggling multiple contractors. We handle all exterior cleaning needs
        under one contract, one point of contact, one predictable monthly rate.
      </p>
      <a
        href="https://wa.me/27690579693"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="about-cta-button"
        className="inline-block px-12 py-4 bg-blackline-accent text-black font-mono text-sm uppercase tracking-widest font-bold hover:bg-white transition-colors rounded-none splash-action"
        onClick={onOpenQuote}
      >
        WhatsApp Us
      </a>
    </div>
  </div>
);

export default function About() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Seo
        title="About | BLACKLINE CLEANING"
        description="About BLACKLINE CLEANING — contract-based exterior cleaning, gutter cleaning and maintenance services across Johannesburg & Sandton."
        canonical="https://www.blackline-cleaning.co.za/about"
        ogImage={BRAND_LOGO_URL}
      />
      <div className="relative pt-40 md:pt-44 pb-24" data-testid="about-page">
      <QuoteModal isOpen={showModal} onClose={() => setShowModal(false)} />
      {/* concrete-texture removed */}
      
      <div className="max-w-5xl mx-auto px-6 py-24 relative z-10">
        <AboutHero />
        <AboutStory />
        <AboutWhatWeDo />
        <AboutWhyContract />
        <AboutAviation />
        <AboutBodyCorporate />
        <AboutClosing onOpenQuote={() => setShowModal(false)} />
      </div>
    </div>
    </>
  );
}